import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { BiDownload, BiErrorCircle, BiHeadphone, BiTimeFive, BiVolumeMute } from "react-icons/bi";
import SpinnerLoader from "../../../components/UI/SpinnerLoader/SpinnerLoader";
import useMediaLinkConversion, { MEDIA_LINK_PHASE } from "../../../hooks/useMediaLinkConversion";
import { isSignedS3Url, canBrowserPlay, MediaLinkCard, DownloadWithNote } from "../MediaLink/mediaLinkShared";

// Bug923: listening page for hashtag audio short links — /a/:code, texted and
// emailed to hashtag admins. No login. On open it checks the audio's status;
// AMR (which no browser plays) is converted to MP3 automatically and played
// here, so the address stays on this Trivvy link. Every other format keeps the
// original behaviour: the page is replaced by the file itself.

const AudioLinkRedirect = ({ match }) => {
  const { phase, linkState, retryAfterSeconds, retry, refresh } = useMediaLinkConversion(match.params.code, true);
  const refreshedUrlRef = useRef("");

  const audioUrl = linkState && isSignedS3Url(linkState.url) ? linkState.url : "";
  const originalDownloadUrl = linkState && isSignedS3Url(linkState.originalDownloadUrl) ? linkState.originalDownloadUrl : "";
  const isNonAmrReady = phase === MEDIA_LINK_PHASE.READY && linkState && !linkState.conversionRequired;
  const redirectsToFile = isNonAmrReady && audioUrl && canBrowserPlay(audioUrl, "audio");

  useEffect(() => {
    if (redirectsToFile) window.location.replace(audioUrl);
  }, [redirectsToFile, audioUrl]);

  // A signed URL expires after an hour: get a fresh one once, without re-converting.
  const handlePlayerError = () => {
    if (refreshedUrlRef.current === audioUrl) return;
    refreshedUrlRef.current = audioUrl;
    refresh();
  };

  const downloadOriginalLink = originalDownloadUrl ? (
    <DownloadWithNote href={originalDownloadUrl} className="media_link_secondary_link">
      <BiDownload className="media_link_button_icon" />
      Download original
    </DownloadWithNote>
  ) : null;

  if (phase === MEDIA_LINK_PHASE.UNAVAILABLE || (phase === MEDIA_LINK_PHASE.READY && !audioUrl)) {
    return (
      <MediaLinkCard
        icon={<BiVolumeMute />}
        muted
        title="Audio not available"
        text="This audio link is invalid or no longer available. The hashtag may have been removed."
      >
        <Link to="/" className="media_link_button">
          Open Trivvy
        </Link>
      </MediaLinkCard>
    );
  }

  if (isNonAmrReady && !redirectsToFile) {
    return (
      <MediaLinkCard
        icon={<BiHeadphone />}
        title="Audio ready"
        text="This audio format can't be played in your browser. Download it to listen on your device."
      >
        <a href={audioUrl} download className="media_link_button">
          <BiDownload className="media_link_button_icon" />
          Download audio
        </a>
      </MediaLinkCard>
    );
  }

  if (phase === MEDIA_LINK_PHASE.READY && !isNonAmrReady) {
    return (
      <MediaLinkCard icon={<BiHeadphone />} title="Audio ready" text="Tap play to listen.">
        <audio controls preload="metadata" src={audioUrl} className="media_link_player" onError={handlePlayerError}>
          Your browser does not support the audio element.
        </audio>
        {downloadOriginalLink}
      </MediaLinkCard>
    );
  }

  if (phase === MEDIA_LINK_PHASE.PROCESSING) {
    return (
      <MediaLinkCard icon={<BiHeadphone />} title="Audio is processing..." text="Converting audio to play in your browser. This can take a moment.">
        <div className="media_link_loader">
          <SpinnerLoader />
        </div>
        {downloadOriginalLink}
      </MediaLinkCard>
    );
  }

  if (phase === MEDIA_LINK_PHASE.STALLED) {
    return (
      <MediaLinkCard icon={<BiTimeFive />} title="Still processing" text="This audio is taking longer than usual. Check again in a little while.">
        <button type="button" className="media_link_button" onClick={refresh}>
          Check again
        </button>
        {downloadOriginalLink}
      </MediaLinkCard>
    );
  }

  if (phase === MEDIA_LINK_PHASE.BUSY) {
    return (
      <MediaLinkCard
        icon={<BiTimeFive />}
        title="Audio processing is busy"
        text={
          "Audio processing is busy. Please try again shortly." +
          (retryAfterSeconds ? " (about " + retryAfterSeconds + " seconds)" : "")
        }
      >
        <button type="button" className="media_link_button" onClick={retry}>
          Retry
        </button>
        {downloadOriginalLink}
      </MediaLinkCard>
    );
  }

  if (phase === MEDIA_LINK_PHASE.FAILED || phase === MEDIA_LINK_PHASE.ERROR) {
    const canRetry = phase === MEDIA_LINK_PHASE.ERROR || (linkState && linkState.canRetry);
    return (
      <MediaLinkCard
        icon={<BiErrorCircle />}
        muted
        title="Audio preview unavailable"
        text={
          originalDownloadUrl
            ? "This audio can't be played here. You can download the original recording."
            : "Something went wrong. Please try again."
        }
      >
        {canRetry ? (
          <button type="button" className="media_link_button" onClick={retry}>
            Retry
          </button>
        ) : null}
        {downloadOriginalLink}
      </MediaLinkCard>
    );
  }

  return (
    <MediaLinkCard icon={<BiHeadphone />} title="Opening audio…" text="Your audio will start in a moment.">
      <div className="media_link_loader">
        <SpinnerLoader />
      </div>
    </MediaLinkCard>
  );
};

export default AudioLinkRedirect;
